import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { uploadOne, fileUrl } from "@/lib/uploads";
import { Camera, CheckCircle2, Loader2, AlertTriangle, Clock, Sparkles, X } from "lucide-react";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + "T23:59:59");
  const diff = Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

const urgencyOf = (snag) => {
  const d = daysUntil(snag.target_completion_date);
  if (d == null) return "normal";
  if (d < 0) return "overdue";
  if (d <= 2) return "urgent";
  return "normal";
};

export default function SnagSupervisorPage() {
  const [snags, setSnags] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/snags/mine");
      setSnags(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so Overdue / Urgent / Pending counts stay live
  // without the supervisor having to reload the page mid-shift.
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const buckets = useMemo(() => {
    const pending = [];
    const overdue = [];
    const urgent = [];
    const done = [];
    for (const s of snags) {
      if (s.status === "verified" || s.status === "resolved") { done.push(s); continue; }
      const u = urgencyOf(s);
      if (u === "overdue") overdue.push(s);
      else if (u === "urgent") urgent.push(s);
      else pending.push(s);
    }
    return { pending, overdue, urgent, done };
  }, [snags]);

  const shown = tab === "overdue" ? buckets.overdue : tab === "urgent" ? buckets.urgent : tab === "done" ? buckets.done : buckets.pending;
  const [q, setQ] = useState("");
  const filteredShown = useMemo(
    () => shown.filter((s) => projectMatches(s, q)),
    [shown, q]
  );

  return (
    <div className="min-h-screen p-4 md:p-8">
      <header className="mb-6 md:mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-1">Phase 8 · My Snag Fixes</p>
        <h1 className="font-display text-2xl md:text-3xl text-white">Pending Fixes Feed</h1>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-[#d4af37]" strokeWidth={1.5} />
          An after-photo is mandatory to close any item.
        </p>
      </header>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {[
          { key: "overdue", label: `Overdue · ${buckets.overdue.length}`, tone: "red" },
          { key: "urgent", label: `Urgent · ${buckets.urgent.length}`, tone: "gold" },
          { key: "pending", label: `Pending · ${buckets.pending.length}`, tone: "neutral" },
          { key: "done", label: `Done · ${buckets.done.length}`, tone: "green" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`sup-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-[11px] font-mono uppercase tracking-widest whitespace-nowrap transition-all ${
              tab === t.key
                ? t.tone === "red"
                  ? "bg-red-500/10 text-red-300 border border-red-500/40"
                  : t.tone === "gold"
                  ? "bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/40"
                  : t.tone === "green"
                  ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40"
                  : "bg-[#1a1a1a] text-white border border-[#333]"
                : "text-gray-500 border border-[#1c1c1c] hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ProjectSearchBar
        value={q}
        onChange={setQ}
        placeholder="Search snags by client, project, or city…"
        resultCount={filteredShown.length}
        totalCount={shown.length}
        testid="snag-sup-search"
      />

      {loading && <div className="p-8 text-center text-xs text-gray-500">Loading…</div>}
      {!loading && shown.length === 0 && (
        <div className="p-12 text-center text-xs text-gray-500 border border-[#1c1c1c] rounded-md bg-[#0a0a0a]">
          Nothing here right now.
        </div>
      )}
      {!loading && shown.length > 0 && filteredShown.length === 0 && q.trim() && (
        <ProjectSearchEmpty query={q} testid="snag-sup-search-empty" />
      )}

      <ul className="space-y-3">
        {filteredShown.map((s) => (
          <SupervisorSnagCard key={s.id} snag={s} onChanged={load} />
        ))}
      </ul>
    </div>
  );
}

function SupervisorSnagCard({ snag, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [afterFile, setAfterFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const days = daysUntil(snag.target_completion_date);
  const urgency = urgencyOf(snag);
  const isDone = snag.status === "resolved" || snag.status === "verified";

  const pickAfter = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const rec = await uploadOne(f);
      setAfterFile(rec);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const markResolved = async () => {
    if (!afterFile) return toast.error("After-photo is mandatory");
    setSubmitting(true);
    try {
      await api.patch(`/snags/${snag.id}/resolve`, {
        after_photo_file_id: afterFile.id,
        resolution_note: note.trim() || null,
      });
      toast.success("Snag marked resolved — awaiting CRM verification");
      setAfterFile(null); setNote(""); setExpanded(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not mark resolved");
    } finally { setSubmitting(false); }
  };

  const toneClass =
    urgency === "overdue" ? "border-red-500/40" :
    urgency === "urgent" ? "border-[#d4af37]/40" :
    isDone ? "border-emerald-500/30" : "border-[#1c1c1c]";

  return (
    <li className={`border ${toneClass} bg-[#0f0f0f] rounded-md`} data-testid={`sup-snag-${snag.id}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-start gap-3">
          <div className={`w-1 self-stretch rounded ${
            urgency === "overdue" ? "bg-red-500" :
            urgency === "urgent" ? "bg-[#d4af37]" :
            isDone ? "bg-emerald-500" : "bg-gray-700"
          }`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white">{snag.title}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
              {snag.category} · {snag.trade_resource_tag || "General"}
            </p>
            <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              Target: <span className="font-mono">{snag.target_completion_date}</span>
              {days != null && (
                <span className={`${
                  urgency === "overdue" ? "text-red-400" :
                  urgency === "urgent" ? "text-[#d4af37]" : "text-gray-500"
                }`}>
                  {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}
                </span>
              )}
              {snag.reject_count > 0 && (
                <span className="text-red-400 font-mono">↩ Rejected {snag.reject_count}×</span>
              )}
            </p>
          </div>
          <span className={`text-[10px] font-mono px-2 py-1 rounded ${
            snag.status === "verified" ? "text-emerald-300 border border-emerald-500/40" :
            snag.status === "resolved" ? "text-[#d4af37] border border-[#d4af37]/40" :
            "text-gray-400 border border-[#333]"
          }`}>
            {snag.status}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#1c1c1c] p-4">
          <p className="text-xs text-gray-300 leading-relaxed">{snag.description}</p>

          {snag.before_photo_file_id && (
            <div className="mt-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Before</p>
              <a href={fileUrl(snag.before_photo_file_id)} target="_blank" rel="noreferrer">
                <img
                  src={fileUrl(snag.before_photo_file_id)}
                  alt="before"
                  className="w-full max-h-56 object-cover border border-[#262626] rounded"
                  data-testid={`before-preview-${snag.id}`}
                />
              </a>
            </div>
          )}

          {snag.resolution_thread && snag.resolution_thread.length > 0 && (
            <div className="mt-4 border-t border-[#1c1c1c] pt-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Activity</p>
              <ul className="space-y-1">
                {snag.resolution_thread.map((t) => (
                  <li key={t.id} className="text-[11px] text-gray-400">
                    <span className={`font-mono ${t.kind === "rejected" ? "text-red-400" : t.kind === "verified" ? "text-emerald-300" : "text-[#d4af37]"}`}>
                      [{t.kind || "note"}]
                    </span>{" "}
                    {t.by_name} — {t.remark}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isDone && (
            <div className="mt-4 border-t border-[#1c1c1c] pt-4 space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={pickAfter}
                data-testid={`after-photo-input-${snag.id}`}
              />
              {!afterFile && (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full iz-btn-secondary flex items-center justify-center gap-2 text-xs"
                  data-testid={`after-photo-btn-${snag.id}`}
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  Upload validation after-photo
                </button>
              )}
              {afterFile && (
                <div className="border border-[#d4af37]/40 bg-[#d4af37]/5 rounded p-2 flex items-center gap-3">
                  <img src={fileUrl(afterFile.id)} alt="after" className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#d4af37] font-mono">After-photo attached</p>
                    <p className="text-[10px] text-gray-500 truncate">{afterFile.original_filename}</p>
                  </div>
                  <button onClick={() => setAfterFile(null)} className="text-gray-500 hover:text-white" data-testid={`clear-after-${snag.id}`}>
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              )}
              <textarea
                className="iz-input w-full min-h-[50px] text-xs"
                placeholder="Optional resolution note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid={`resolve-note-${snag.id}`}
              />
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  {!afterFile && (
                    <p className="text-[10px] text-gray-500 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-[#d4af37]" strokeWidth={1.5} />
                      Toggle is locked until an after-photo is uploaded.
                    </p>
                  )}
                </div>
                <button
                  onClick={markResolved}
                  disabled={!afterFile || submitting}
                  data-testid={`mark-resolved-${snag.id}`}
                  className={`px-4 py-2 rounded-md text-xs flex items-center gap-2 transition-all ${
                    afterFile
                      ? "bg-[#d4af37] text-black hover:brightness-110"
                      : "bg-[#1a1a1a] text-gray-600 cursor-not-allowed"
                  }`}
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  Mark Resolved
                </button>
              </div>
            </div>
          )}

          {snag.status === "resolved" && snag.after_photo_file_id && (
            <div className="mt-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">After (awaiting CRM verify)</p>
              <a href={fileUrl(snag.after_photo_file_id)} target="_blank" rel="noreferrer">
                <img src={fileUrl(snag.after_photo_file_id)} alt="after" className="w-full max-h-56 object-cover border border-[#d4af37]/40 rounded" />
              </a>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
